import type { CSSProperties, FC, KeyboardEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { setMenuOpen } from '@/lib/universe/store'
import { UmbraLink } from '@/components/generals/umbra-link'
import { useMenu } from '@/components/generals/menu-context'
import { NAVIGATION, siteConfig } from '@/constants/site'

const pathOf = (value: string): string => value.split(/[?#]/)[0].replace(/\/+$/, '') || '/'

// A black room with the hole small and quiet on the right, and three words on the left
export const MenuOverlay: FC = (): ReactNode => {
    const { open, setOpen } = useMenu()
    const router = useRouter()
    const [mounted, setMounted] = useState(false)
    const [visible, setVisible] = useState(false)
    const [hovered, setHovered] = useState<number | null>(null)
    const dialogRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const returnFocusRef = useRef<HTMLElement | null>(null)
    const current = pathOf(router.asPath)

    useEffect(() => {
        setMenuOpen(open)

        if (open) {
            returnFocusRef.current = document.activeElement as HTMLElement | null
            document.documentElement.style.overflow = 'hidden'
            setMounted(true)

            let inner = 0
            const outer = window.requestAnimationFrame(() => {
                inner = window.requestAnimationFrame(() => setVisible(true))
            })

            return () => {
                window.cancelAnimationFrame(outer)
                window.cancelAnimationFrame(inner)
            }
        }

        setVisible(false)
        setHovered(null)
        document.documentElement.style.overflow = ''

        const timer = window.setTimeout(() => setMounted(false), 700)

        return () => window.clearTimeout(timer)
    }, [open])

    useEffect(() => {
        if (visible) closeRef.current?.focus({ preventScroll: true })
    }, [visible])

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events, setOpen])

    const close = (): void => {
        setOpen(false)
        returnFocusRef.current?.focus?.({ preventScroll: true })
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Escape') {
            event.preventDefault()
            close()

            return
        }

        if (event.key !== 'Tab') return

        const focusables = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])

        if (focusables.length === 0) return

        const first = focusables[0]
        const last = focusables[focusables.length - 1]

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    if (!mounted) return null

    return (
        <div
            ref={dialogRef}
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            onKeyDown={onKeyDown}
            data-menu-visible={visible ? 'true' : 'false'}
            className={cn(
                'fixed inset-0 z-50 overflow-hidden bg-void/30 transition-opacity duration-700',
                visible ? 'opacity-100' : 'opacity-0'
            )}
        >
            <div className='flex items-start justify-between px-6 pt-6 md:px-10 md:pt-8'>
                <UmbraLink
                    href='/'
                    aria-label='VOIDEN, home'
                    onClick={event => {
                        if (current === '/') event.preventDefault()

                        setOpen(false)
                    }}
                    className='label-caps text-[0.75rem] tracking-[0.42em] text-flare transition-opacity duration-500 hover:opacity-70'
                >
                    VOIDEN
                </UmbraLink>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={close}
                    className='label-caps link-line text-flare'
                >
                    Close
                </button>
            </div>

            <div className='absolute inset-x-0 top-1/2 -translate-y-1/2 px-6 md:px-[7vw]'>
                <nav aria-label='Main'>
                    <ul className='flex flex-col gap-[3.5vh] md:gap-[4.5vh]' onMouseLeave={() => setHovered(null)}>
                        {NAVIGATION.map((item, index) => {
                            const exact = current === item.href
                            const active = exact || current.startsWith(`${item.href}/`)
                            const dimmed = hovered !== null && hovered !== index

                            return (
                                <li key={item.href}>
                                    <UmbraLink
                                        href={item.href}
                                        aria-current={exact ? 'page' : undefined}
                                        onClick={event => {
                                            if (exact) event.preventDefault()

                                            setOpen(false)
                                        }}
                                        onMouseEnter={() => setHovered(index)}
                                        onFocus={() => setHovered(index)}
                                        onBlur={() => setHovered(null)}
                                        style={{ '--item-delay': `${180 + index * 120}ms` } as CSSProperties}
                                        className='umbra-menu-item block w-max'
                                    >
                                        <span
                                            className={cn(
                                                'display-serif block text-[clamp(3.75rem,13vw,11rem)] transition-[color,opacity] duration-700',
                                                hovered === index ? 'text-flare' : active ? 'text-foreground' : 'text-foreground/80',
                                                dimmed && 'opacity-25'
                                            )}
                                        >
                                            {item.label}
                                        </span>
                                    </UmbraLink>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <a
                    href={`mailto:${siteConfig.email}`}
                    style={{ '--item-delay': `${180 + NAVIGATION.length * 120 + 200}ms` } as CSSProperties}
                    className='umbra-menu-item link-line mt-[7vh] inline-block font-sans text-[0.75rem] tracking-[0.06em] text-muted-foreground transition-colors duration-500 hover:text-foreground'
                >
                    {siteConfig.email}
                </a>
            </div>
        </div>
    )
}
